import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent } from 'react';

const RUN_MS = 30_000;
const SPEED_PER_MS = 0.012;
const OBJECT_START_X = 62;
const FAKE_POLICE_START_X = [96, 158, 220, 282, 344] as const;
// A half-second collision window at the game's scroll speed: enough to read
// the obstacle, but forgiving enough that a phone tap is not frame-perfect.
const COLLISION_FROM = 16;
const COLLISION_TO = 22;
const JUMP_MS = 1_100;

type RunState = 'idle' | 'running' | 'caught' | 'won' | 'missed';

const REWARDS = [
  'an off-menu HGR cassette',
  'a silver backstage wristband',
  'the last cold peach soda',
] as const;

function xStyle(x: number): CSSProperties {
  return { '--chase-x': `${x}%` } as CSSProperties;
}

/**
 * A tiny, deliberately local arcade game inside a real presence room.
 *
 * The room, its people and its messages live on the server. A run does not:
 * refreshing or leaving is the same as walking away from an arcade cabinet,
 * and avoids turning a playful thirty seconds into another account ledger.
 */
export function ArcadeChase({ playerName }: { playerName: string }) {
  const [runState, setRunState] = useState<RunState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [jumping, setJumping] = useState(false);
  const [hasObject, setHasObject] = useState(false);
  const [reward, setReward] = useState<string>(REWARDS[0]);
  const startedAt = useRef(0);
  const jumpingRef = useRef(false);
  const jumpTimer = useRef<number | null>(null);
  const cabinet = useRef<HTMLDivElement | null>(null);

  const jump = useCallback(() => {
    if (runState !== 'running' || jumpingRef.current) return;
    jumpingRef.current = true;
    setJumping(true);
    if (jumpTimer.current !== null) window.clearTimeout(jumpTimer.current);
    jumpTimer.current = window.setTimeout(() => {
      jumpingRef.current = false;
      setJumping(false);
      jumpTimer.current = null;
    }, JUMP_MS);
  }, [runState]);

  const start = useCallback(() => {
    if (jumpTimer.current !== null) window.clearTimeout(jumpTimer.current);
    startedAt.current = performance.now();
    jumpingRef.current = false;
    setJumping(false);
    setElapsed(0);
    setHasObject(false);
    setReward(REWARDS[Math.floor(Date.now() / 1000) % REWARDS.length]);
    setRunState('running');
    window.setTimeout(() => cabinet.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (runState !== 'running') return;

    const tick = window.setInterval(() => {
      const nextElapsed = Math.min(RUN_MS, performance.now() - startedAt.current);
      const travelled = nextElapsed * SPEED_PER_MS;
      const objectX = OBJECT_START_X - travelled;

      setElapsed(nextElapsed);
      if (!hasObject && objectX >= COLLISION_FROM && objectX <= COLLISION_TO) {
        setHasObject(true);
      }

      const hit = FAKE_POLICE_START_X.some((startX) => {
        const x = startX - travelled;
        return x >= COLLISION_FROM && x <= COLLISION_TO;
      });
      if (hit && !jumpingRef.current) {
        setRunState('caught');
        return;
      }

      if (nextElapsed >= RUN_MS) {
        setRunState(hasObject || objectX < COLLISION_FROM ? 'won' : 'missed');
      }
    }, 40);

    return () => window.clearInterval(tick);
  }, [hasObject, runState]);

  useEffect(() => () => {
    if (jumpTimer.current !== null) window.clearTimeout(jumpTimer.current);
  }, []);

  const handleKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === ' ' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      jump();
    }
  };

  const travelled = elapsed * SPEED_PER_MS;
  const objectX = OBJECT_START_X - travelled;
  const secondsLeft = Math.max(0, Math.ceil((RUN_MS - elapsed) / 1000));
  const progress = Math.min(100, (elapsed / RUN_MS) * 100);

  return (
    <div
      ref={cabinet}
      className="chat-chase"
      data-state={runState}
      tabIndex={0}
      onKeyDown={handleKey}
      onClick={(event) => event.stopPropagation()}
      aria-label="The Getaway, a thirty second jump game"
    >
      <div className="chat-chase__hud mono">
        <span><b>TIME</b> {secondsLeft.toString().padStart(2, '0')}</span>
        <span><b>MISSION</b> {hasObject ? 'BAG ✓' : 'GET THE BAG'}</span>
        <span><b>RUN</b> {Math.round(progress)}%</span>
      </div>

      <div className="chat-chase__sky" aria-hidden="true">
        <i /><i /><i /><b>HGR AFTER HOURS</b>
      </div>
      <div className="chat-chase__city" aria-hidden="true"><i /><i /><i /><i /><i /><i /></div>
      <div className="chat-chase__track" aria-hidden="true">
        <i /><i /><i /><i /><i /><i />
      </div>

      <span
        className="chat-chase__runner"
        data-jumping={jumping ? 'true' : undefined}
        aria-hidden="true"
      >
        <i /><b /><em /><strong />
        {hasObject ? <small>▣</small> : null}
      </span>

      {!hasObject && objectX > -12 ? (
        <span className="chat-chase__object" style={xStyle(objectX)} aria-hidden="true">
          <i>HGR</i><b>LOST RECORD BAG</b>
        </span>
      ) : null}

      {FAKE_POLICE_START_X.map((startX, index) => {
        const x = startX - travelled;
        if (x < -18 || x > 118) return null;
        return (
          <span className="chat-chase__fakecop" style={xStyle(x)} aria-hidden="true" key={startX}>
            <i /><b /><em /><strong />
            <small>{index % 2 === 0 ? 'FAKE' : 'POLICE?'}</small>
          </span>
        );
      })}

      {elapsed > 25_000 || runState === 'won' ? (
        <span className="chat-chase__friend" aria-hidden="true">
          <i /><b /><em /><strong />
          <small>{runState === 'won' ? 'YOU MADE IT!' : 'OVER HERE!'}</small>
        </span>
      ) : null}

      {runState === 'idle' ? (
        <div className="chat-chase__card">
          <span className="mono">THE GETAWAY · 30 SECONDS</span>
          <h3>Get the record bag. Dodge the fake police. Reach your friend.</h3>
          <p>They are party actors in terrible costumes. Press space, ↑ or the jump button.</p>
          <button type="button" onClick={start}>Start run</button>
        </div>
      ) : null}

      {runState === 'caught' ? (
        <div className="chat-chase__card chat-chase__card--result" role="status">
          <span className="mono">CAUGHT BY A VERY UNCONVINCING OFFICER</span>
          <h3>The moustache was drawn on.</h3>
          <p>You can go again immediately.</p>
          <button type="button" onClick={start}>Retry</button>
        </div>
      ) : null}

      {runState === 'missed' ? (
        <div className="chat-chase__card chat-chase__card--result" role="status">
          <span className="mono">YOU ARRIVED EMPTY-HANDED</span>
          <h3>The record bag is still back there.</h3>
          <button type="button" onClick={start}>Run again</button>
        </div>
      ) : null}

      {runState === 'won' ? (
        <div className="chat-chase__card chat-chase__card--result" role="status">
          <span className="mono">SAFE · {playerName.toUpperCase()}</span>
          <h3>Your friend gives you {reward}.</h3>
          <p>Thirty seconds, one bag, zero real police.</p>
          <button type="button" onClick={start}>Play again</button>
        </div>
      ) : null}

      <button
        type="button"
        className="chat-chase__jump mono"
        disabled={runState !== 'running'}
        onClick={jump}
        aria-label="Jump over the fake police"
      >
        {jumping ? 'AIR!' : 'JUMP'}
      </button>

      <span className="sr-only" aria-live="polite">
        {runState === 'running'
          ? `${secondsLeft} seconds left. ${hasObject ? 'Record bag collected.' : 'Find the record bag.'}`
          : runState === 'caught'
            ? 'Caught. Retry when ready.'
            : runState === 'won'
              ? `You reached your friend and received ${reward}.`
              : ''}
      </span>
    </div>
  );
}
